import React from "react"
import { useRecoilState } from "recoil"
import currentAssociation from "../../store/currentAssociation"
import currentUser from "../../store/currentUser"
import { useSnackbar } from "notistack"
import { API, graphqlOperation } from "aws-amplify"
import { createInvite as CreateInvite } from "../../graphqlCustom/mutations"
import { deleteInvite as DeleteInvite } from "../../graphqlCustom/mutations"
import { listInvites } from "../../graphqlCustom/queries"
import { onCreateInvite } from "../../graphqlCustom/subscriptions"
import { sendEmail } from "../../graphqlCustom/queries"

import { makeStyles } from "@material-ui/core/styles"
import Grid from "@material-ui/core/Grid"
import IconButton from "@material-ui/core/IconButton"
import AddIcon from "@material-ui/icons/Add"
import DeleteIcon from "@material-ui/icons/Delete"

import { formatDate } from "../../libs/format"
import Button from "../../components/Button"
import Card from "../../components/Card"
import Layout from "../../components/AuthenticatedLayout"
import FormContainer from "../../components/FormContainer"
import TextField from "../../components/TextField"

const useStyles = makeStyles((theme) => ({
  addButton: {
    float: "right",
    marginBottom: theme.spacing(5),
  },
  form: {
    "&:after": {
      clear: "both",
      content: "",
      display: "block",
    },
  },
  root: {
    clear: "both",
  },
  submit: {},
  formHeaderContent: {
    paddingLeft: theme.spacing(2),
    margin: 0,
  },
  items: {
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: 640,
    padding: theme.spacing(1),
  },
  content: {
    fontSize: 16,
    whiteSpace: "pre-line",
  },
  author: {
    fontSize: 12,
    marginBottom: 0,
    padding: 0,
  },
}))

const year = new Date().getFullYear()
const message = `
Hey there,

[fromEmail] has invited you to join their Condo Axis home owner's association.

Use the link to join now:
<a href="${window.location.origin.toString()}/signup/invite/[inviteId]">${window.location.origin.toString()}/signup/invite/[inviteId]</a>

Condo Axis is a collaboration platform for better HOA management. Condo Axis simplifies communication and better organizes documents.

We hope you enjoy Condo Axis!

This email was sent to [toEmail], which is associated with a <a href="https://condoaxis.com">Condo Axis</a> account.

&copy; ${year} Condo Axis Inc., All Rights Reserved
Condo Axis Inc., 3805 N Lincoln Ave, Chicago, IL 60613
`

const initialState = {
  items: [],
}

const emailInvite = (toEmail, fromEmail, inviteId, associationName) => {
  API.graphql(
    graphqlOperation(sendEmail, {
      from: "messages@hi.condoaxis.com",
      to: toEmail,
      subject: `${fromEmail} has invited you to join their Condo Axis home owner's association: ${associationName}`,
      content: message
        .replace(/\n/g, "<br/>")
        .replace(/\[inviteId\]/g, inviteId)
        .replace(/\[fromEmail\]/g, fromEmail)
        .replace(/\[toEmail\]/g, toEmail),
      replyTo: fromEmail,
    })
  )
}

function reducer(state, action) {
  switch (action.type) {
    case "SET_INVITES":
      return { ...state, items: action.items }
    case "ADD_INVITE":
      return { ...state, items: [action.item, ...state.items] }
    case "REMOVE_INVITE":
      const newItems = state.items.filter(function (obj) {
        return obj.id !== action.inviteId
      })
      return { ...state, items: newItems }
    default:
      return state
  }
}

const Invite = () => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [fields, setFields] = React.useState({ email: "", role: "" })
  const [state, dispatch] = React.useReducer(reducer, initialState)

  const [association] = useRecoilState(currentAssociation)
  const [user] = useRecoilState(currentUser)

  React.useEffect(() => {
    async function fetchItems() {
      try {
        const items = await API.graphql(
          graphqlOperation(listInvites, {
            filter: {
              inviteAssociationId: {
                eq: association.id,
              },
            },
            limit: 200,
            sortDirection: "DESC",
          })
        )
        dispatch({ type: "SET_INVITES", items: items.data.listInvites.items })
      } catch (error) {
        console.log("error", error)
      }
    }
    fetchItems()
    const subscription = API.graphql(graphqlOperation(onCreateInvite)).subscribe({
      next: (response) => {
        const item = response.value.data.onCreateInvite
        if (item) {
          dispatch({ type: "ADD_INVITE", item })
        }
      },
    })
    return () => subscription.unsubscribe()
  }, [association])

  const onChange = (e) => {
    setFields({
      ...fields,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    createInvite()
  }

  async function createInvite() {
    if (!fields.email) {
      enqueueSnackbar("Please add an email", {
        variant: "error",
      })
      return // alert("Please add an email")
    }

    let exists = false
    if (state.items.length) {
      state.items.map((item, index) => {
        if (item.email === fields.email) {
          exists = true
        }
        return item
      })
    }

    if (exists) {
      enqueueSnackbar("There is already an active invite for that email", {
        variant: "error",
      })
      return
    }

    if (fields.email) {
      const submitData = {
        inviteAssociationId: association.id,
        email: fields.email,
        owner: user.id,
        role: "MEMBER",
      }

      try {
        // Add the invite
        const response = await API.graphql(graphqlOperation(CreateInvite, { input: submitData }))

        if (response.data && response.data.createInvite) {
          emailInvite(fields.email, user.email, response.data.createInvite.id, association.name)
        }

        // Reset form
        setFields({ email: "" })

        enqueueSnackbar("Invite successfully sent", {
          variant: "success",
        })
      } catch (error) {
        console.log("error", error)
        enqueueSnackbar("Invite could not be sent", {
          variant: "error",
        })
      }
    }
  }

  async function deleteInvite(inviteId) {
    try {
      const response = await API.graphql(
        graphqlOperation(DeleteInvite, { input: { id: inviteId } })
      )
      if (response.data && response.data.deleteInvite) {
        dispatch({ type: "REMOVE_INVITE", inviteId })
      }

      // Reset form

      enqueueSnackbar("Invite successfully removed", {
        variant: "success",
      })
    } catch (error) {
      console.log("error", error)
      enqueueSnackbar(`Invite could not be deleted`, {
        variant: "error",
      })
    }
  }

  return (
    <Layout title="Assocation Invites">
      <FormContainer fullWidth>
        <form
          className={classes.form}
          onSubmit={(e) => {
            e.preventDefault()
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <h3 className={classes.formHeaderContent}>Invite a Member</h3>
              <p className={classes.formHeaderContent}>
                Send an invitation to join the association
              </p>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="email"
                label="Email Address"
                value={fields.email}
                onChange={onChange}
                type="email"
              />
            </Grid>
          </Grid>
          <Grid container spacing={3} justifyContent="flex-end">
            <Grid item>
              <Button className={classes.submit} startIcon={<AddIcon />} onClick={handleSubmit}>
                Send Invite
              </Button>
            </Grid>
          </Grid>
          <div style={{ clear: "both", content: "", display: "block" }}></div>
        </form>
      </FormContainer>
      <FormContainer fullWidth transparent>
        {state.items.length ? (
          state.items.map((item, index) => {
            const inviteLink = `https://${window.location.hostname}/signup/invite/${item.id}`
            return (
              <Card title={item.email}>
                <Grid container spacing={3} key={index} className={classes.item}>
                  <Grid item xs={11}>
                    {/* <p className={classes.content}>{item.email}</p> */}
                    <p className={classes.author}>
                      Invited by: {item.author.firstName} {item.author.lastName} &lt;
                      {item.author.email}
                      &gt; on {formatDate(item.createdAt)}
                    </p>
                  </Grid>
                  <Grid item xs={1} justifyContent="flex-end">
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault()
                        deleteInvite(item.id)
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12}>
                    Invite Link: <a href="{inviteLink}">{inviteLink}</a>
                  </Grid>
                </Grid>
              </Card>
            )
          })
        ) : (
          <h3>No invites yet.</h3>
        )}
      </FormContainer>
    </Layout>
  )
}

export default Invite
